import { createSelector, createSlice } from '@reduxjs/toolkit'
import { RepaymentMethods } from 'share/enums/payment'
import type {
  ApplicationCreditStatuses,
  ApplicationFlowTypes,
  ApplicationProgramCode,
  ApplicationSteps,
  CurrentStepFlowStatuses,
  CurrentStepStatuses,
  FLOW_STEPS,
  Nullable
} from 'share/types'
import type { GetApplicationStatusResponse, LoanPurposes } from 'share/types/api/main'
import { type UserRecord } from 'share/types/model/user'
import { APP_STEPS } from 'utils/constants'

import { type RootState } from '..'

type InitialState = {
  id: Nullable<string | number>
  current_step: {
    id: Nullable<string | number>
    step: Nullable<ApplicationSteps>
    retry_at: Nullable<number>
    status: Nullable<CurrentStepStatuses>
    flow_type: Nullable<ApplicationFlowTypes>
    flow_status: Nullable<CurrentStepFlowStatuses>
    status_reason: Nullable<string>
    status_reason_kk: Nullable<string>
    can_retry: Nullable<boolean>
    created_at: Nullable<string>
    depends_on_colvir_worktime: Nullable<boolean>
    data: unknown
  } | null
  next_step: Nullable<ApplicationSteps>
  is_form_income: Nullable<boolean>
  credit_program: Nullable<ApplicationProgramCode>
  credit_program_verbose: Nullable<string>
  credit_program_verbose_kk: Nullable<string>
  credit_status: Nullable<ApplicationCreditStatuses>
  colvir_available_at: Nullable<string>
  is_colvir_worktime: Nullable<boolean>
  loan_purpose: Nullable<LoanPurposes>
  skip_corp_card_creation: Nullable<boolean>
  next_step_depends_on_colvir_worktime: Nullable<boolean>
  third_party_pledger: Pick<UserRecord, 'first_name' | 'middle_name' | 'last_name' | 'iin' | 'phone'> | null
  need_spouse_consent: Nullable<boolean>
  borrower_record: {
    id: Nullable<number>
    first_name: Nullable<string>
    last_name: Nullable<string>
    middle_name: Nullable<string>
    iin: Nullable<string>
    gender: 'FEMALE' | 'MALE' | undefined | null
    document_number: Nullable<string | number>
    marital_status: UserRecord['marital_status']
    phone: string | null
  }
  credit_params: {
    gesv: Nullable<string | number>
    interest_rate: Nullable<string | number>
    loan_amount: Nullable<string | number>
    monthly_payment: Nullable<string | number>
    period: Nullable<number>
    repayment_method: Nullable<RepaymentMethods>
    pay_day: Nullable<number>
    insurance_sum: Nullable<number>
    credit_sum: Nullable<number>
  }
  is_success: Nullable<boolean>
}

const initialState: InitialState = {
  id: null,
  current_step: {
    id: null,
    step: null,
    retry_at: null,
    status: null,
    flow_type: null,
    flow_status: null,
    status_reason: null,
    status_reason_kk: null,
    can_retry: null,
    created_at: null,
    data: null,
    depends_on_colvir_worktime: null
  },
  next_step: null,
  is_form_income: null,
  credit_program: null,
  credit_program_verbose: null,
  credit_program_verbose_kk: null,
  credit_status: null,
  colvir_available_at: null,
  is_colvir_worktime: null,
  skip_corp_card_creation: null,
  next_step_depends_on_colvir_worktime: null,
  loan_purpose: null,
  third_party_pledger: null,
  need_spouse_consent: null,
  borrower_record: {
    id: null,
    first_name: null,
    last_name: null,
    middle_name: null,
    iin: null,
    gender: null,
    document_number: null,
    marital_status: null,
    phone: null
  },
  credit_params: {
    gesv: null,
    interest_rate: null,
    loan_amount: null,
    monthly_payment: null,
    period: null,
    repayment_method: null,
    pay_day: null,
    insurance_sum: null,
    credit_sum: null
  },
  is_success: null
}

const statusSlice = createSlice({
  name: 'statusSlice',
  initialState,
  reducers: {
    setStatusData(state, { payload }: { payload: GetApplicationStatusResponse }) {
      state.id = payload.id
      state.current_step = payload.current_step
      state.next_step = payload.next_step
      state.is_form_income = payload.is_form_income
      state.credit_program = payload.credit_program
      state.credit_status = payload.credit_status
      state.credit_program_verbose = payload.credit_program_verbose
      state.credit_program_verbose_kk = payload.credit_program_verbose_kk
      state.borrower_record = payload.borrower_record
      state.credit_params = payload.credit_params
      state.is_success = payload.is_success
      state.colvir_available_at = payload.colvir_available_at
      state.is_colvir_worktime = payload.is_colvir_worktime
      state.skip_corp_card_creation = payload.skip_corp_card_creation
      state.next_step_depends_on_colvir_worktime = payload.next_step_depends_on_colvir_worktime
      state.loan_purpose = payload.loan_purpose
      state.third_party_pledger = payload.third_party_pledger
      state.need_spouse_consent = payload.need_spouse_consent
    }
  }
})

export const { setStatusData } = statusSlice.actions

export const getStatusSelector = (state: RootState) => state.status
export const getCreditParamsSelector = (state: RootState) => state.status.credit_params
export const getCreditProgramSelector = (state: RootState) => state.status.credit_program
export const getCurrentFlowType = (state: RootState) => state.status.current_step?.flow_type
export const getThirdPartyPledgerSelector = (state: RootState) => state.status.third_party_pledger

const selectCurrentStep = (state: RootState) => state.status.current_step
const selectIsSuccess = (state: RootState) => state.status.is_success
const selectIsLoanIssuedStatus = (state: RootState) => state.status.credit_status === 'ISSUED'

// export const legacy_getCurrentStepNumberSelector = createSelector(
//   selectCurrentStep,
//   selectIsSuccess,
//   (currentStep, isFlowSuccessfullyEnded) => {
//     if (currentStep?.flow_type !== 'MAIN_FLOW' && isFlowSuccessfullyEnded)
//       return {
//         isSuccess: true,
//         step: 5,
//         title: ''
//       }
//     const step = appSteps.find(el => el.apiStep.includes(currentStep?.step as FLOW_STEPS))
//     if (!step)
//       return {
//         isSuccess: false,
//         step: 1,
//         title: ''
//       }
//     return {
//       isSuccess: false,
//       step: step.step,
//       title: step.title
//     }
//   }
// )

export const getCurrentStepNumberSelector = createSelector(
  selectIsLoanIssuedStatus,
  selectCurrentStep,
  selectIsSuccess,
  (selectLoanIssuedStatus, currentStep, isFlowSuccessfullyEnded) => {
    if ((currentStep?.flow_type !== 'MAIN_FLOW' && isFlowSuccessfullyEnded) || selectLoanIssuedStatus)
      return {
        isSuccess: true,
        step: 5,
        title: ''
      }
    const step = APP_STEPS.findLast(el => el.apiStep.includes(currentStep?.step as FLOW_STEPS))
    if (!step)
      return {
        isSuccess: false,
        step: 1,
        title: ''
      }
    return {
      isSuccess: false,
      step: step.step,
      title: step.title
    }
  }
)

export default statusSlice.reducer
